<!--
  Modal "Solicitar CIEC al cliente" (ADR-0032, B-075).
  Patrón canónico de p-dialog del repo: header simple, content + footer en templates,
  modal + dismissableMask, footer con acciones primary/cancel.
-->
<p-dialog
  [(visible)]="visible"
  (visibleChange)="visibleChange.emit($event)"
  header="Solicitar CIEC al cliente"
  [style]="{ width: '90vw', maxWidth: '760px' }"
  [modal]="true"
  [dismissableMask]="true"
  styleClass="ciec-request-dialog"
>
  <ng-template pTemplate="content">
    <!-- ─── STEP CONFIRM ──────────────────────────────────────────────── -->
    <ng-container *ngIf="step === 'confirm'">
      <div class="confirm-body">
        <!-- Aviso de la solicitud más reciente: qué pasó con el último enlace y qué hacer. -->
        <div
          *ngIf="banner as b"
          class="request-banner"
          [ngClass]="'is-' + b.tone"
          role="status"
          data-test="request-banner"
          [attr.data-kind]="b.kind"
        >
          <i
            class="fa-solid"
            aria-hidden="true"
            [ngClass]="b.tone === 'info' ? 'fa-circle-info' : b.tone === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-xmark'"
          ></i>
          <div [ngSwitch]="b.kind">
            <ng-container *ngSwitchCase="'pending'">
              <strong>Solicitud pendiente</strong>
              <span
                >Enviada el {{ b.request.createdAt | date: 'dd/MM/yy HH:mm'
                }}<ng-container *ngIf="b.request.sentTo?.email"> a {{ b.request.sentTo?.email }}</ng-container
                >. Vence el {{ b.request.expiresAt | date: 'dd/MM/yy' }}.</span
              >
            </ng-container>
            <ng-container *ngSwitchCase="'rejected'">
              <strong>El SAT rechazó la CIEC {{ b.request.attemptsUsed }} de {{ b.request.maxAttempts }} veces</strong>
              <span *ngIf="lastSatReason(b.request) as reason">Último motivo: «{{ reason }}».</span>
              <span>El enlace sigue vivo. Pídele al cliente que confirme su CIEC en el portal del SAT antes de volver a intentar.</span>
            </ng-container>
            <ng-container *ngSwitchCase="'exhausted'">
              <strong>El cliente agotó los {{ b.request.maxAttempts }} intentos</strong>
              <span *ngIf="lastSatReason(b.request) as reason">Último motivo del SAT: «{{ reason }}».</span>
              <span
                >El enlace se cerró el {{ b.request.lastAttempt?.at || b.request.updatedAt | date: 'dd/MM/yy HH:mm' }}. Confirma con el
                cliente su CIEC vigente antes de enviarle un enlace nuevo.</span
              >
            </ng-container>
            <ng-container *ngSwitchCase="'provider'">
              <strong>El último intento falló de nuestro lado</strong>
              <span
                >El {{ b.request.lastAttempt?.at | date: 'dd/MM/yy HH:mm' }} la consulta al SAT falló por el proveedor o por nosotros, no
                por la CIEC del cliente. Puede reintentar con el mismo enlace; si se repite, avisa a TI.</span
              >
            </ng-container>
            <ng-container *ngSwitchCase="'rfc'">
              <strong>El RFC del cliente no es válido</strong>
              <span>Con ese RFC el SAT no puede validar la CIEC. Corrígelo en «Editar cliente»: el enlace sigue sirviendo.</span>
              <button
                pButton
                type="button"
                class="p-button-sm p-button-outlined banner-action"
                icon="fa-solid fa-pen"
                label="Editar cliente"
                (click)="requestEditClient()"
              ></button>
            </ng-container>
            <ng-container *ngSwitchCase="'pending-sat'">
              <strong>CIEC recibida · el SAT aún no la confirma</strong>
              <span>Si el SAT la rechaza, el enlace se reabre solo para que el cliente la corrija.</span>
            </ng-container>
            <ng-container *ngSwitchCase="'no-credential'">
              <strong>El enlace se usó, pero el SAT nunca validó la CIEC</strong>
              <span>El análisis que dejó salió sin credencial del SAT y su monto no refleja capacidad. Envía un enlace nuevo.</span>
            </ng-container>
            <ng-container *ngSwitchCase="'legacy-failed'">
              <strong>La última solicitud no se completó</strong>
              <span>{{ outcomeLabel(b.request).label }}. Envía un enlace nuevo.</span>
            </ng-container>
          </div>
        </div>

        <!-- Sin correo registrado no hay a dónde mandar el enlace y el botón de envío
             queda deshabilitado. Antes solo se veía un guion, así que el usuario no
             tenía forma de saber ni por qué ni cómo resolverlo. -->
        <div class="no-email-warning" *ngIf="!clientEmail">
          <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
          <div>
            <strong>Este cliente no tiene correo registrado.</strong>
            <span>
              Agrégaselo desde &laquo;Editar cliente&raquo; para poder enviarle el enlace. Mientras tanto puedes usar &laquo;Ingresar
              CIEC&raquo; en el análisis fiscal si ya la tienes a la mano.
            </span>
          </div>
        </div>
        <p class="lead" *ngIf="clientEmail">
          Se enviará un correo a
          <strong>{{ clientEmail }}</strong>
          con un enlace seguro para que el cliente suba su CIEC del SAT.
        </p>
        <ul class="info-list">
          <li>
            <i class="fa-solid fa-clock" aria-hidden="true"></i>
            <span>El enlace es válido por <strong>7 días</strong> y admite hasta <strong>3 rechazos del SAT</strong>.</span>
          </li>
          <li>
            <i class="fa-solid fa-link" aria-hidden="true"></i>
            <span>También podrás copiar el enlace para enviarlo por otro canal (WhatsApp, SMS).</span>
          </li>
          <li>
            <i class="fa-solid fa-shield-halved" aria-hidden="true"></i>
            <span>La CIEC se guarda cifrada solo si el SAT la valida, para revalidar sin pedirla otra vez.</span>
          </li>
        </ul>

        <!-- Historial completo: el servidor ya interpreta cada solicitud (`outcome`). -->
        <section class="history" aria-labelledby="ciec-history-title">
          <h4 id="ciec-history-title">
            <i class="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
            Solicitudes anteriores
          </h4>

          <div *ngIf="historyState === 'loading'" class="history-loading" data-test="history-loading" aria-busy="true">
            <p-skeleton height="1.6rem" styleClass="mb-2"></p-skeleton>
            <p-skeleton height="1.6rem" styleClass="mb-2"></p-skeleton>
            <p-skeleton height="1.6rem"></p-skeleton>
          </div>

          <div *ngIf="historyState === 'error'" class="history-error" role="alert" data-test="history-error">
            <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
            <span>{{ historyError }}</span>
            <button
              pButton
              type="button"
              class="p-button-sm p-button-text"
              icon="fa-solid fa-arrows-rotate"
              label="Reintentar"
              (click)="loadHistory()"
            ></button>
          </div>

          <div *ngIf="historyState === 'ready' && requests.length === 0" class="history-empty" data-test="history-empty">
            <i class="fa-solid fa-inbox" aria-hidden="true"></i>
            <span>Aún no se le ha pedido la CIEC a este cliente.</span>
          </div>

          <table class="history-table" *ngIf="historyState === 'ready' && requests.length > 0" data-test="history-table">
            <thead>
              <tr>
                <th scope="col">Enviada</th>
                <th scope="col">Estado</th>
                <th scope="col">Intentos</th>
                <th scope="col">Último motivo</th>
                <th scope="col">Vence / usada</th>
                <th scope="col"><span class="p-hidden-accessible">Acciones</span></th>
              </tr>
            </thead>
            <tbody *ngFor="let r of requests; trackBy: trackById" data-test="history-row">
              <tr>
                <td data-label="Enviada">
                  <span class="cell-main">{{ r.createdAt | date: 'dd/MM/yy HH:mm' }}</span>
                  <small class="cell-sub" data-test="requested-by">por {{ requesterName(r) }}</small>
                </td>
                <td data-label="Estado">
                  <p-tag
                    [value]="outcomeLabel(r).label"
                    [severity]="outcomeLabel(r).severity"
                    [styleClass]="outcomeLabel(r).severity === 'secondary' ? 'ciec-tag-neutral' : ''"
                    data-test="outcome-tag"
                  ></p-tag>
                </td>
                <td data-label="Intentos" class="cell-attempts">{{ attemptsLabel(r) }}</td>
                <td data-label="Último motivo">
                  <ng-container *ngIf="r.lastAttempt as last; else noAttempt">
                    <span class="cell-main">{{ attemptLabel(last).label }}</span>
                    <small class="cell-sub" *ngIf="last.satReason">«{{ last.satReason }}»</small>
                    <small class="cell-hint" *ngIf="attemptLabel(last).hint">{{ attemptLabel(last).hint }}</small>
                  </ng-container>
                  <ng-template #noAttempt><span class="cell-muted">—</span></ng-template>
                </td>
                <td data-label="Vence / usada">
                  <ng-container [ngSwitch]="r.status">
                    <span *ngSwitchCase="'pending'">Vence {{ r.expiresAt | date: 'dd/MM/yy' }}</span>
                    <span *ngSwitchCase="'used'">Usada {{ r.usedAt | date: 'dd/MM/yy' }}</span>
                    <span *ngSwitchCase="'cancelled'">Cancelada {{ r.cancelledAt | date: 'dd/MM/yy' }}</span>
                    <span *ngSwitchCase="'exhausted'">Cerrada {{ r.lastAttempt?.at || r.updatedAt | date: 'dd/MM/yy' }}</span>
                    <span *ngSwitchDefault>Venció {{ r.expiresAt | date: 'dd/MM/yy' }}</span>
                  </ng-container>
                </td>
                <td class="cell-actions">
                  <button
                    *ngIf="r.attempts?.length"
                    pButton
                    type="button"
                    class="p-button-sm p-button-text p-button-secondary"
                    [icon]="isExpanded(r) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
                    [attr.aria-expanded]="isExpanded(r)"
                    [attr.aria-controls]="'ciec-attempts-' + r._id"
                    [attr.aria-label]="(isExpanded(r) ? 'Ocultar' : 'Ver') + ' los intentos de la solicitud'"
                    data-test="toggle-attempts"
                    (click)="toggleAttempts(r)"
                  ></button>
                  <button
                    *ngIf="canResend(r)"
                    pButton
                    type="button"
                    class="p-button-sm p-button-text"
                    icon="fa-solid fa-paper-plane"
                    pTooltip="Reenviar (genera un enlace nuevo)"
                    tooltipPosition="top"
                    aria-label="Reenviar la solicitud con un enlace nuevo"
                    data-test="resend-request"
                    [disabled]="loading || !clientEmail"
                    [loading]="loading && busyRequestId === r._id"
                    (click)="resendRequest(r)"
                  ></button>
                  <button
                    *ngIf="r.status === 'pending'"
                    pButton
                    type="button"
                    class="p-button-sm p-button-text p-button-danger"
                    icon="fa-solid fa-ban"
                    pTooltip="Cancelar"
                    tooltipPosition="top"
                    aria-label="Cancelar la solicitud"
                    data-test="cancel-request"
                    [disabled]="!!busyRequestId"
                    (click)="cancelRequest(r)"
                  ></button>
                </td>
              </tr>
              <tr class="attempts-row" [id]="'ciec-attempts-' + r._id" [hidden]="!isExpanded(r)" data-test="attempts-detail">
                <td colspan="6">
                  <ol class="attempts-list">
                    <li *ngFor="let a of r.attempts">
                      <span class="attempt-date">{{ a.at | date: 'dd/MM HH:mm' }}</span>
                      <span class="attempt-sep" aria-hidden="true">·</span>
                      <span class="attempt-label" [ngClass]="'is-' + attemptLabel(a).severity">{{ attemptLabel(a).label }}</span>
                      <ng-container *ngIf="a.satReason">
                        <span class="attempt-sep" aria-hidden="true">·</span>
                        <span class="attempt-reason">«{{ a.satReason }}»</span>
                      </ng-container>
                      <ng-container *ngIf="a.code">
                        <span class="attempt-sep" aria-hidden="true">·</span>
                        <span class="attempt-count">{{ a.counted ? 'cuenta para el tope' : 'no cuenta' }}</span>
                      </ng-container>
                    </li>
                  </ol>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- Error con código al generar o reenviar: se queda aquí, junto a la acción que lo resuelve. -->
        <div
          *ngIf="generateError as e"
          class="request-banner"
          [ngClass]="e.severity === 'info' ? 'is-info' : e.severity === 'warn' ? 'is-warn' : 'is-danger'"
          role="alert"
          data-test="generate-error"
        >
          <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
          <div>
            <strong>{{ e.summary }}</strong>
            <span>{{ e.detail }}</span>
            <button
              *ngIf="e.action === 'edit-rfc'"
              pButton
              type="button"
              class="p-button-sm p-button-outlined banner-action"
              icon="fa-solid fa-pen"
              label="Editar cliente"
              (click)="requestEditClient()"
            ></button>
          </div>
        </div>

        <!-- Antes de reemplazar: el enlace vivo deja de servir y el contador de rechazos vuelve a cero. -->
        <div *ngIf="replacesPrevious" class="replace-notice" role="note" data-test="replace-notice">
          <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
          <div>
            <span *ngIf="activeRequest as active">Esto invalida el enlace enviado el {{ active.createdAt | date: 'dd/MM/yy' }}.</span>
            <span *ngIf="previousRejections > 0" data-test="previous-rejections"
              >El enlace anterior acumuló {{ previousRejections }} {{ previousRejections === 1 ? 'rechazo' : 'rechazos' }} del SAT: confirma
              la CIEC con el cliente antes de enviarle otro.</span
            >
          </div>
        </div>

        <!-- B-075 · Con CIEC guardada, pedir otra solo tiene sentido si el cliente la cambió o el SAT la rechaza. -->
        <div *ngIf="ciecStatus?.stored" class="replace-notice" role="note" data-test="stored-notice">
          <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
          <div>
            <span
              >Este cliente ya tiene una CIEC guardada<ng-container *ngIf="ciecStatus?.updatedAt as storedAt">
                desde el {{ storedAt | date: 'dd/MM/yy' }}</ng-container
              >. Envía un enlace nuevo solo si la cambió o si el SAT la rechaza.</span
            >
          </div>
        </div>
      </div>
    </ng-container>

    <!-- ─── STEP SENT ─────────────────────────────────────────────────── -->
    <ng-container *ngIf="step === 'sent'">
      <div class="sent-body">
        <div class="sent-banner">
          <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
          <div>
            <strong>Correo enviado a {{ sentToEmail }}</strong>
            <span *ngIf="expiresAt">Vence el {{ expiresAt | date: 'dd MMM yyyy' }}</span>
          </div>
        </div>

        <label class="link-label" for="ciec-request-link">Enlace para compartir manualmente</label>
        <div class="link-field">
          <input id="ciec-request-link" pInputText type="text" [value]="generatedLink" readonly (focus)="$any($event.target).select()" />
          <button pButton type="button" icon="fa-solid fa-copy" label="Copiar" class="p-button-sm" (click)="copyLink()"></button>
        </div>
        <small class="link-hint">
          También puedes mandarlo por WhatsApp o SMS. Caduca el
          <strong>{{ expiresAt | date: 'dd MMM yyyy' }}</strong
          >.
        </small>
      </div>
    </ng-container>
  </ng-template>

  <ng-template pTemplate="footer">
    <ng-container *ngIf="step === 'confirm'">
      <button
        pButton
        type="button"
        label="Cancelar"
        icon="pi pi-times"
        class="p-button-outlined"
        [disabled]="loading"
        (click)="close()"
      ></button>
      <button
        pButton
        type="button"
        [label]="generateLabel"
        icon="fa-solid fa-paper-plane"
        data-test="generate"
        [loading]="loading && !busyRequestId"
        [disabled]="loading || !clientEmail"
        (click)="generate()"
      ></button>
    </ng-container>
    <ng-container *ngIf="step === 'sent'">
      <button
        pButton
        type="button"
        label="Reenviar correo"
        icon="fa-solid fa-arrows-rotate"
        class="p-button-outlined"
        [loading]="loading"
        [disabled]="loading"
        (click)="resend()"
      ></button>
      <button pButton type="button" label="Cerrar" icon="pi pi-check" (click)="close()"></button>
    </ng-container>
  </ng-template>
</p-dialog>
